import { DatePipe, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import * as Highcharts from 'highcharts';
import { debounceTime, fromEvent } from 'rxjs';

import { CropFieldAdvice } from '../disease-overview-table.component/models/models';

import { DiseaseInsightChartStore } from './disease-insight-chart.store';
import { DiseaseChartDay } from './models';
import { AgrChartModule } from '@next/core-lib/chart';

@Component({
  selector: 'app-disease-insight-chart',
  standalone: true,
  templateUrl: './disease-insight-chart.component.html',
  styleUrl: './disease-insight-chart.component.scss',
  providers: [DiseaseInsightChartStore],
  imports: [TranslatePipe, AgrChartModule, DatePipe, NgTemplateOutlet, NgOptimizedImage],
})
export class DiseaseInsightChartComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly localeService = inject(LocaleService);

  protected readonly store = inject(DiseaseInsightChartStore);

  // --- Signal inputs ---
  protected readonly chartDays = input<DiseaseChartDay[]>([]);
  protected readonly selectedRange = input<Date[]>([]);
  protected readonly advice = input<CropFieldAdvice>();
  protected readonly riskTableRowsTemplate =
    input<TemplateRef<{ $implicit: DiseaseChartDay[]; chart: DiseaseInsightChartComponent }>>();
  protected readonly dateHeaderAccessoryTemplate = input<TemplateRef<{ $implicit: DiseaseChartDay }>>();
  protected readonly betweenChartAndTableTemplate = input<TemplateRef<{ chart: DiseaseInsightChartComponent }>>();
  protected readonly plantDateInFutureMessageKey = input('shared.insightChart.plantDateInFuture');
  protected readonly noAdviceAvailableMessageKey = input('shared.insightChart.getNewDataMessage');
  protected readonly showDataWithoutAdvices = input(false);
  protected readonly dataWithoutAdvicesMessageKey = input('shared.insightChart.harvestedNoAdvice');

  // --- Signal output ---
  protected readonly actionTriggered = output<{ event: MouseEvent; date: Date }>();
  protected readonly seriesToggled = output<{ seriesName: string; visible: boolean }>();

  // --- UI-only state ---
  protected tooltipText: string | null = null;
  protected showCustomTooltip = false;
  protected mouseX = 0;
  protected mouseY = 0;
  protected tableMarginLeft = signal(0);
  protected tableMarginRight = signal(0);
  private chartInstance: Highcharts.Chart | undefined;
  private readonly removeLegendClickListeners: Array<() => void> = [];
  protected readonly showSkeleton = computed(() => this.advice() === undefined);
  protected readonly hasGraphData = computed(() =>
    this.store
      .chartDataSeries()
      .some((series) => 'data' in series && Array.isArray(series.data) && series.data.length > 0),
  );
  protected readonly skeletonColumns = Array.from({ length: 11 });
  protected readonly skeletonRows = Array.from({ length: 3 });

  constructor() {
    effect(() => this.store.setChartDays(this.chartDays()));
    effect(() => this.store.setSelectedRange(this.selectedRange()));
    effect(() => this.store.setAdvice(this.advice()));
  }

  ngOnInit(): void {
    Highcharts.AST.allowedAttributes.push('alt');

    fromEvent(window, 'resize')
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.chartInstance) {
          this.chartInstance.reflow();
          this.updateTableMargins();
        }
      });
  }

  ngOnDestroy(): void {
    this.removeLegendClickListeners.forEach((removeListener) => removeListener());
    this.removeLegendClickListeners.length = 0;
  }

  getUnprotectedGrowthBackground(unprotectedGrowth: number): string {
    return `rgb(181, 6, 26, ${unprotectedGrowth})`;
  }

  getTuberInfectionRiskBackground(tuberInfectionRisk: number): string {
    return tuberInfectionRisk ? 'risk-warn' : 'risk-none';
  }

  getOoSporesRiskBackground(ooSporesRisk: boolean): string {
    return ooSporesRisk ? 'risk-warn' : 'risk-none';
  }

  isToday(date: Date): boolean {
    if (!date) return false;
    const today = new Date();
    const diseaseDate = new Date(date);
    return diseaseDate.toLocaleDateString() === today.toLocaleDateString();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    this.mouseX = event.clientX + 10;
    this.mouseY = event.clientY + 10;
  }

  showTooltip(_event: MouseEvent, text: string): void {
    this.tooltipText = text;
    this.showCustomTooltip = true;
  }

  showAdviceTooltip(event: MouseEvent, tooltipKey: string | null | undefined): void {
    if (tooltipKey) {
      const translatedText = this.localeService.translate(tooltipKey);
      this.showTooltip(event, translatedText);
    }
  }

  hideTooltip(): void {
    this.tooltipText = null;
    this.showCustomTooltip = false;
  }

  protected onAction(event: MouseEvent, date: Date): void {
    event.stopPropagation();
    const actionDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0));
    this.actionTriggered.emit({ event, date: actionDate });
  }

  onChartCreated(chart: Highcharts.Chart): void {
    this.chartInstance = chart;
    this.updateTableMargins();
    this.attachLegendTracking(chart);
  }

  private updateTableMargins(): void {
    if (this.chartInstance) {
      this.tableMarginLeft.set(this.chartInstance.plotLeft);
      this.tableMarginRight.set(
        this.chartInstance.chartWidth - this.chartInstance.plotLeft - this.chartInstance.plotWidth,
      );
    }
  }

  private attachLegendTracking(chart: Highcharts.Chart): void {
    this.removeLegendClickListeners.forEach((removeListener) => removeListener());
    this.removeLegendClickListeners.length = 0;

    chart.series.forEach((series) => {
      const seriesId = String(series.options.id ?? '');
      if (!seriesId || seriesId === 'showAll' || seriesId === 'emergenceDate') {
        return;
      }

      const removeListener = Highcharts.addEvent(series, 'legendItemClick', () => {
        this.seriesToggled.emit({
          seriesName: series.name,
          visible: !series.visible,
        });
      });

      this.removeLegendClickListeners.push(() => removeListener());
    });
  }
}
